import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { useGroupMembersContext } from "@context-providers/group-members-provider";
import { SdbMemberTypes } from "@custom-types/member-types";
import { neutral } from "@flat-ui";
import { Divider, Stack } from "@mui/material";
import { ChangeManagementDialog } from "@pages/project-details/project-overview/change-management/change-management-dialog";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { getBottomBorderWithBoxShadow } from "@utils/ui-utils";
import { getUserTooltipDisplayName } from "@utils/user-utils";

interface Props {
  /** The project manager to display */
  projectManager: {
    user: SdbMemberTypes | null;
    name: string;
  };
}

/**
 * A field to display the project manager and the group of a project. Clicking the field opens a dialog to change them.
 * The project manager is displayed on the right side and the group on the left.
 */
export function GroupAndProjectManagerField({
  projectManager,
}: Props): JSX.Element {
  const { isUpdatingProjects } = useAppSelector(fetchingProjectsFlagsSelector);

  const { project, canChangeManagement } = useGroupMembersContext();

  return (
    <ChangeManagementDialog>
      <Stack
        sx={
          canChangeManagement
            ? {
                "&:hover": {
                  boxShadow: getBottomBorderWithBoxShadow({
                    color: neutral[400],
                  }),
                },
              }
            : undefined
        }
        direction="row"
        spacing={0}
      >
        <div style={{ width: "50%" }}>
          <FaroTextField
            label="Group"
            initialValue={project.group?.name ?? "No Group"}
            faroVariant="main"
            fontSize="18px"
            shouldShowTooltip
            cursor={canChangeManagement ? "pointer" : undefined}
            isFullWidth={true}
            isDisabled={isUpdatingProjects}
          />
        </div>
        <Divider
          orientation="vertical"
          flexItem
          variant="middle"
          sx={{ mx: "10px", mt: "26px" }}
        />
        <div style={{ width: "50%" }}>
          <FaroTextField
            label="Project Manager"
            initialValue={projectManager.name}
            faroVariant="main"
            fontSize="18px"
            shouldShowTooltip
            tooltipProps={{
              title: getUserTooltipDisplayName({ member: projectManager.user }),
            }}
            cursor={canChangeManagement ? "pointer" : undefined}
            isFullWidth={true}
            isDisabled={isUpdatingProjects}
            isLoading={isUpdatingProjects}
          />
        </div>
      </Stack>
    </ChangeManagementDialog>
  );
}
